import React, { useContext, useEffect, useState } from 'react';

import { createStyles, makeStyles, Theme, withStyles } from '@material-ui/core/styles';
import { GridRowParams } from '@mui/x-data-grid';

import CustomDataGrid from '../../components/data-grid';
import JobLogButton from '../../components/job-log-button';
import JobRunTicker from '../../components/job-run ticker';
import JobRunButton from '../../components/job-run-button';
import Screen from '../../components/screen';
import SplashWait from '../../components/splash-wait';
import { AppContext } from '../../contexts/app-context';
import GlobalStyles from '../../global-styles';
import {
  apiRequestError, canReadPendingEnrollment, canWriteEnrollmentFile, getBreadcrumbMaxItems,
  getShowDashboardValue, goToRoutePage, setWindowTitle, toggleShowDashboard
} from '../../utils/helper';
import {
  AddFileIcon, DashboardOffIcon, DashboardOnIcon, MoreIcon, RefreshIcon
} from '../../utils/icons';
import {
  Box, Breadcrumbs, Button, Card, CardContent, CardHeader, Grid, Hidden, IconButton, Link,
  ListItemIcon, ListItemText, Menu, MenuItem, Tooltip, Typography
} from '../../utils/material-ui-core';

const Enrollment = () => {
  const pageStyles = LocalPageStyles();
  const globalStyles = GlobalStyles();

  const { showAlert, pendingEnrollmentCompanies, refreshData, userFunctionAccess } = useContext(AppContext);

  const [loading, setLoading] = useState<boolean>(true);
  const [showDashboard, setShowDashboard] = useState<boolean>(true);
  const [pendingEnrollmentCompanyData, setPendingEnrollmentCompanyData] = useState<PendingEnrollmentCompany[]>([]);
  const [gridMoreAnchorEl, setGridMoreAnchorEl] = React.useState(null);

  const columns = [
    { field: "companyCode", headerName: "Code", width: 150 },
    { field: "companyName", headerName: "Company Name", width: 250 },
    { field: "fileCount", headerName: "File#", width: 150, type: "number" },
    { field: "recordCount", headerName: "Record#", width: 150, type: "number" },
    {
      field: "pendingCount",
      headerName: "Pending#",
      width: 150,
      type: "number",
    },
    {
      field: "inQueueCount",
      headerName: "In Queue#",
      width: 150,
      type: "number",
    },
    { field: "holdCount", headerName: "On Hold #", width: 150, type: "number" },
    { field: "companyKey", headerName: "Company Key", width: 150, hide: true },
  ];

  const handleGridMoreMenuClick = (event) => {
    setGridMoreAnchorEl(event.currentTarget);
  };

  const handleGridMoreMenuClose = () => {
    setGridMoreAnchorEl(null);
  };

  const refresh = async () => {
    setLoading(true);
    await refreshData("pec");
    getData();
  };

  const getData = async () => {
    try {
      setLoading(true);
      setPendingEnrollmentCompanyData([]);

      const rows: any = [];
      pendingEnrollmentCompanies.map((pendingEnrollmentCompany, index) =>
        rows.push({
          id: index,
          ...pendingEnrollmentCompany,
        })
      );
      setPendingEnrollmentCompanyData(rows);
      setLoading(false);
    } catch (e) {
      setLoading(false);
      showAlert({
        message: apiRequestError(e, "Something went wrong in our system."),
        severity: "error",
      });
    }
  };

  const handleDataGridClick = async (params: GridRowParams, event: any) => {
    goToRoutePage("enrollmentcorrection/companies/" + params.row.companyKey + "/batches");
  };

  const StyledMenuItem = withStyles((theme) => ({
    root: {
      "&:focus": {
        backgroundColor: theme.palette.secondary.main,
        "& .MuiListItemIcon-root, & .MuiListItemText-primary": {
          color: theme.palette.common.white,
        },
      },
    },
  }))(MenuItem);

  useEffect(() => {
    setWindowTitle("Enrollment Correction - Companies");
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const initialPageAsync = async () => {
      if (!canReadPendingEnrollment(userFunctionAccess)) {
        showAlert({ message: "Access Denied.", severity: "error" });
        goToRoutePage("home");
        return;
      }

      setShowDashboard(getShowDashboardValue("ShowEnrollmentDashboard", true));
      getData();
    };

    initialPageAsync();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEnrollmentCompanies]);

  return (
    <>
      {loading ? (
        <SplashWait />
      ) : (
        <Screen>
          <Box className={pageStyles.root}>
            <Grid container spacing={3}>
              <Grid item xs={9}>
                <Box className={globalStyles.bottomSpacer}>
                  <Breadcrumbs aria-label="breadcrumb" maxItems={getBreadcrumbMaxItems(2)}>
                    <Link color="inherit" onClick={() => goToRoutePage("home")} className={globalStyles.link}>
                      Home
                    </Link>
                    <Typography color="textPrimary">Enrollment Correction</Typography>
                    <Typography color="textPrimary">Companies</Typography>
                  </Breadcrumbs>
                </Box>
              </Grid>
              <Grid item xs={3}>
                <Hidden xsDown>
                  <Box display="flex" justifyContent="flex-end" width={1}>

                    <IconButton onClick={() => setShowDashboard(toggleShowDashboard("ShowEnrollmentDashboard", showDashboard))}>
                      <Tooltip arrow title={!showDashboard ? "Show Dashboard" : "Hide Dashboard"}>
                        {!showDashboard ? <DashboardOffIcon color="secondary" /> : <DashboardOnIcon color="secondary" />}
                      </Tooltip>
                    </IconButton>

                    <JobLogButton jobPrefix='enrollment' jobName='Enrollment' mode={0} handleGridMoreMenuClose={() => { }} />

                    <JobRunButton jobName={"Enrollment"} mode={0} fileId={0} />

                    {
                      canWriteEnrollmentFile(userFunctionAccess) ?
                        <IconButton>
                          <Tooltip arrow title={"Upload new enrollment file."}>
                            <AddFileIcon color="secondary" onClick={() => goToRoutePage('enrollment/files/open')} />
                          </Tooltip>
                        </IconButton>

                        : null
                    }

                    <IconButton>
                      <Tooltip arrow title={"Refresh"}>
                        <RefreshIcon color="secondary" onClick={() => refresh()} />
                      </Tooltip>
                    </IconButton>
                  </Box>
                </Hidden>

                <Hidden smUp>
                  <Box display="flex" justifyContent="flex-end" width={1}>
                    <Tooltip arrow title="Menu">
                      <Button color="secondary" aria-controls="fade-menu" aria-haspopup="true" onClick={handleGridMoreMenuClick}>
                        <MoreIcon />
                      </Button>
                    </Tooltip>
                    <Menu elevation={2} id="grid-menu" anchorEl={gridMoreAnchorEl} keepMounted open={Boolean(gridMoreAnchorEl)} onClose={handleGridMoreMenuClose}>

                      <StyledMenuItem
                        onClick={(e) => {
                          setShowDashboard(toggleShowDashboard("ShownEnrollmentDashboard", showDashboard))
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          {!showDashboard ? <DashboardOffIcon fontSize="small" /> : <DashboardOnIcon fontSize="small" />}
                        </ListItemIcon>
                        <ListItemText primary={!showDashboard ? "Show Dashboard" : "Hide Dashboard"} />
                      </StyledMenuItem>

                      <JobLogButton jobPrefix='enrollment' jobName='Enrollment' mode={1} handleGridMoreMenuClose={handleGridMoreMenuClose} />

                      <JobRunButton jobName={"New Enrollment"} mode={2} fileId={0} />

                      {
                        canWriteEnrollmentFile(userFunctionAccess) ?
                          <StyledMenuItem
                            onClick={(e) => {
                              goToRoutePage('enrollment/files/open')
                              handleGridMoreMenuClose();
                            }}
                          >
                            <ListItemIcon>
                              <AddFileIcon fontSize="small" />
                            </ListItemIcon>
                            <ListItemText primary="Upload new enrollment file" />
                          </StyledMenuItem>
                          : null
                      }

                      <StyledMenuItem
                        onClick={(e) => {
                          refresh();
                          handleGridMoreMenuClose();
                        }}
                      >
                        <ListItemIcon>
                          <RefreshIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={"Refresh "} />
                      </StyledMenuItem>
                    </Menu>
                  </Box>
                </Hidden>
              </Grid>
            </Grid>

            <Grid container spacing={3}>
              <Grid item xs={12} sm={12} md={showDashboard ? 8 : 12} lg={showDashboard ? 9 : 12}>
                <Box className={pageStyles.datagridContainer}>
                  <CustomDataGrid uniqueGridCode={"PendingEnrollmentCompaniesResult"} columns={columns} rows={pendingEnrollmentCompanyData} showToolBar={true} sortModel={[{ field: "companyName", sort: "asc" }]} handleDataGridClick={handleDataGridClick} />
                </Box>
              </Grid>
              {
                showDashboard ?
                  <Grid item xs={12} sm={12} md={4} lg={3}>
                    <Grid container spacing={3}>
                      <Grid item xs={12}>
                        <JobRunTicker interval={30} unit={'minutes'} mode={0} />
                      </Grid>
                      <Grid item xs={12} sm={6} md={12} lg={12}>
                        <Card elevation={0} className={globalStyles.rightCardContainer}>
                          <CardHeader title="" subheader="STATUS SUMMARY" />
                          <CardContent>
                            <Grid container>
                              <Grid item xs={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box>
                                    <Typography variant="h6" align="center" className="count">
                                      {pendingEnrollmentCompanies.length > 0 ? (
                                        <>
                                          {pendingEnrollmentCompanies
                                            .map(function (pendingEnrollmentCompanie) {
                                              return pendingEnrollmentCompanie.fileCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box >
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      Files
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box >
                                    <Typography variant="h6" align="center" className="count">
                                      {pendingEnrollmentCompanies.length > 0 ? (
                                        <>
                                          {pendingEnrollmentCompanies
                                            .map(function (pendingEnrollmentCompanie) {
                                              return pendingEnrollmentCompanie.recordCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box>
                                    <Typography variant="caption" align="center" className={globalStyles.toggleFontColor}>
                                      Records
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box >
                                    <Typography variant="h6" align="center" className="count">
                                      {pendingEnrollmentCompanies.length > 0 ? (
                                        <>
                                          {pendingEnrollmentCompanies
                                            .map(function (pendingEnrollmentCompanie) {
                                              return pendingEnrollmentCompanie.pendingCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>

                                  </Box>
                                  <Box >
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      Pending
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box >
                                    <Typography variant="h6" align="center" className="count">
                                      {pendingEnrollmentCompanies.length > 0 ? (
                                        <>
                                          {pendingEnrollmentCompanies
                                            .map(function (pendingEnrollmentCompanie) {
                                              return pendingEnrollmentCompanie.inQueueCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box >
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      In Queue
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                              <Grid item xs={12}>
                                <Box display="flex" flexDirection="column" justifyItems='center' alignItems='center' textAlign="center" >
                                  <Box >
                                    <Typography variant="h6" align="center" className="count">
                                      {pendingEnrollmentCompanies.length > 0 ? (
                                        <>
                                          {pendingEnrollmentCompanies
                                            .map(function (pendingEnrollmentCompanie) {
                                              return pendingEnrollmentCompanie.holdCount;
                                            })
                                            .reduce(function (totalCount, count) {
                                              return totalCount + count;
                                            })}
                                        </>
                                      ) : (
                                        0
                                      )}
                                    </Typography>
                                  </Box>
                                  <Box >
                                    <Typography variant="caption" className={globalStyles.toggleFontColor}>
                                      On Hold
                                    </Typography>
                                  </Box>
                                </Box>
                              </Grid>
                            </Grid>
                          </CardContent>
                        </Card>
                        <Box p="3">&nbsp;</Box>
                        <Box p="3">&nbsp;</Box>
                      </Grid>
                    </Grid>
                  </Grid>
                  : null
              }
            </Grid>
          </Box>
        </Screen>
      )}
    </>
  );
};

const LocalPageStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      padding: 0,
    },
    datagridContainer: {
      height: "70vh",
      width: "100%",
      marginBottom: theme.spacing(2),
    },
  })
);

export default Enrollment;
